import axios, { type AxiosRequestConfig, type ResponseType } from 'axios';
import { Notify } from 'quasar'


export interface IViaCepResponse {
  cep?: string
  logradouro?: string
  complemento?: string
  bairro?: string
  localidade?: string
  uf?: string
  erro?: boolean
}

export const httpAxios = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/v1`,
  headers: {
    'Content-Type': 'application/json',
  }
});

httpAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(new Error(error));
});

const headers = { ...httpAxios.defaults.headers };
export const notifyError = (message: string, type: 'error' | 'warning' = 'error') => {
  Notify.create({
    progress: true,
    message,
    color: type === 'error' ? 'red' : 'orange',
    textColor: 'white',
    position: 'top-right',
    icon: type === 'error' ? 'warning' : 'fa-solid fa-circle-info',
    html: true
  });
};



export const _execute = async (
  method: string,
  url: string,
  data: any = null,
  returnFullResponse = false,
  responseType: ResponseType = 'json',
  params?: Record<string, any>,
  axiosInstance: any = httpAxios
) => {
  if (!method) throw new Error('HTTP method is required');
  if (!url) throw new Error('HTTP url is required');

  const config: AxiosRequestConfig = {
    method,
    url,
    responseType
  };

  if (params) {
    config.params = params;
    config.paramsSerializer = {
      serialize: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach(val => searchParams.append(key, val));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      }
    };
  }

  if (method.toLowerCase() !== 'get' && data !== null && data !== undefined) {
    config.data = data;
  }
  return axiosInstance(config)
    .then((response: { data: any; }) => returnFullResponse ? response : response.data)
    .catch((req: { response: { status: any; data: any; }; message: any; }) => {
      const codeError = req?.response?.status;
      const data = req.response?.data;

      console.error('❌ Axios Error:', req);

      if (codeError == 400) {
        Notify.create({
          progress: true,
          message: `${data?.Message}`,
          color: 'orange',
          textColor: 'white',
          position: 'top-right',
          icon: 'fa-solid fa-circle-info',
          html: true
        });
      } 
      else if (codeError === 401) {
        notifyError('Usuário não autorizado. Login ou senha incorretos.');
      } else if (codeError === 403) {
        notifyError('Acesso negado.');
      }
      else {
        notifyError(`${req.message} <br> ${data?.Message} || 'Erro desconhecido'}`);
      }
      throw req;
    })
    .finally(() => {
      axiosInstance.defaults.headers = { ...headers };
      axiosInstance.defaults.headers['Content-Type'] = 'application/json';
      axiosInstance.defaults.responseType = 'json';
    });
};


export const _downloadFile = (file: any, isDownload: boolean, response?: any) => {
  const byteCharacters = atob(file.fileContents)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: file.contentType })
  if (isDownload) {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = file.fileDownloadName
    link.click()
    window.URL.revokeObjectURL(url)
  }
  return { file, byteArray: byteCharacters, blob, response }
}

export const createHttpClient = (axiosInstance: any) => ({
  get<T>(endPoint: string, id?: string | number, params?: Record<string, any>): Promise<T> {
    if (!endPoint) throw new Error('http: EndPoint obrigatório no GET');
    const url = id ? `/${endPoint}/${id}` : `/${endPoint}`;
    return _execute('get', url, null, false, 'json', params, axiosInstance);
  },

  list<T>(endPoint: string, data?: any, id?: string | number): Promise<T> {
    if (!endPoint) throw new Error('http: EndPoint obrigatório no LIST');
    const url = id ? `/${endPoint}/${id}` : `/${endPoint}`;
    return _execute('post', url, data ?? {}, false, 'json', undefined, axiosInstance);
  },

  post<T>(endPoint: string, data?: any): Promise<T> {
    if (!endPoint) throw new Error('http: EndPoint obrigatório no POST');
    return _execute('post', `/${endPoint}`, data ?? {}, false, 'json', undefined, axiosInstance);
  },

  put<T>(endPoint: string, data?: any): Promise<T> {
    if (!endPoint) throw new Error('http: EndPoint obrigatório no PUT');
    return _execute('put', `/${endPoint}`, data ?? {}, false, 'json', undefined, axiosInstance);
  },

  patch<T>(endPoint: string, data?: any, id?: string | number): Promise<T> {
    if (!endPoint) throw new Error('http: EndPoint obrigatório no PATCH');
    const url = id ? `/${endPoint}/${id}` : `/${endPoint}`;
    return _execute('patch', url, data ?? {}, false, 'json', undefined, axiosInstance);
  },

  delete<T>(endPoint: string, id: string | number, params?: Record<string, any>): Promise<T> {
    if (!endPoint) throw new Error('http: EndPoint obrigatório no DELETE');
    if (!id) throw new Error('http: ID obrigatório no DELETE');
    return _execute('delete', `/${endPoint}/${id}`, null, false, 'json', params, axiosInstance);
  },

  upload (file: File, endPoint: string = 'media/uploadFile', description?: string) {
    const fileUpload = Array.isArray(file) ? file[0] : file;
    const formData = new FormData();
    formData.append('file', fileUpload);

    if (description)
      formData.append('description', description);

    axiosInstance.defaults.headers['Content-Type'] = 'multipart/form-data';
    return _execute('post', endPoint, formData, false, 'json', undefined, axiosInstance);
  },

  async downloadAsBlob(options: {
    endPoint: string,
    data?: any,
    fileName?: string | null,
    contentType?: string,
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete',
    params?: Record<string, any>,
    /** Quando false, retorna o Blob sem disparar o download automático via link. Default: true. */
    download?: boolean
  }): Promise<Blob> {
    if (!options.endPoint) throw new Error('http: EndPoint obrigatório para download');

    axiosInstance.defaults.headers['Accept'] = 'application/octet-stream';
    const response = await _execute(options.method || 'post', `/${options.endPoint}`, options.data, true, 'blob', options.params, axiosInstance);

    const contentTypeExtensions: Record<string, string> = {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-excel': '.xls',
      'application/pdf': '.pdf',
      'text/csv': '.csv',
      'application/json': '.json',
      'text/plain': '.txt',
      'application/zip': '.zip'
    };

    let downloadFileName = options.fileName || `download_${new Date().toISOString().replace(/[:.]/g, '-')}`;
    const fileType = options.contentType || 'application/octet-stream';

    const hasExtension = downloadFileName.lastIndexOf('.') > 0;

    if (!hasExtension) {
      const extension = contentTypeExtensions[fileType] || '.file';
      downloadFileName += extension;
    }

    if (options.download !== false) {
      const blob = new Blob([response.data], { type: fileType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return response.data;
  },



  download(id:string) {
    return _execute('get', `media/download/${id}`, null, false, 'json', undefined, axiosInstance);
  },

  async downloadFile(id: string, fileName?: string): Promise<void> {
    try {
      const response = await _execute('get', `media/download/${id}`, null, false, 'json', undefined, axiosInstance);
      const file = response?.file ?? response;

      if (!file?.fileContents) throw new Error('Conteúdo do arquivo não encontrado na resposta');

      if (fileName) {
        file.fileDownloadName = fileName;
      }

      _downloadFile(file, true);

      Notify.create({
        progress: true,
        message: 'Download realizado com sucesso!',
        color: 'positive',
        textColor: 'white',
        position: 'top-right',
        icon: 'fa-solid fa-circle-check'
      });
    } catch (error) {
      Notify.create({
        progress: true,
        message: 'Não foi possível realizar o download do arquivo.',
        color: 'negative',
        textColor: 'white',
        position: 'top-right',
        icon: 'fa-solid fa-circle-xmark'
      });
      throw error;
    }
  },

  downloadByAlert(id:string) {
    return _execute('get', `media/download/${id}/new`, null, false, 'json', undefined, axiosInstance);
  },

  getAddressByCep(cep: string): Promise<IViaCepResponse> {
    if (!cep) throw new Error('http: CEP obrigatorio para consulta');
    const cleanCep = String(cep).replace(/\D/g, '');

    if (cleanCep.length !== 8) throw new Error('http: CEP invalido para consulta');

    return axios
      .get<IViaCepResponse>(`https://viacep.com.br/ws/${cleanCep}/json/`, {
        headers: {
          Accept: 'application/json'
        }
      })
      .then((response) => response.data);
  },

  async getBlobByUrl(url: string): Promise<Blob> {
    if (!url) throw new Error('http: URL obrigatoria para buscar arquivo');

    axiosInstance.defaults.headers['Accept'] = 'application/octet-stream';
    const response = await _execute('get', url, null, true, 'blob', undefined, axiosInstance);
    const contentType = response.headers?.['content-type'] || 'application/octet-stream';

    return new Blob([response.data], { type: contentType });
  },

  async archive(endPoint: string, data?: {}, isDownload: boolean = true): Promise<{ file: any, byteArray: string, blob: Blob, response: any }> {
    const response = await _execute('post', `/${endPoint}`, data, true, 'json', undefined, axiosInstance);
    if (response.data.file && response.data.file.file) {
      return _downloadFile(response.data.file.file, isDownload, response)
    }
    else {
      const { file } = response.data;
      return _downloadFile(file, isDownload, response);
    }
  },
});

export const http = createHttpClient(httpAxios);


const client = {
	http: http,
  httpAxios: httpAxios,
  install(vue: any, parameters: any) {
    parameters.http = http;
    parameters.httpAxios = httpAxios;
	}
};

export default client;
